// In-memory workspace tab state (PRD-0085, slice 1; per-brand in PR3).
//
// Thin React wrapper around the pure reducer in `lib/workspace-tabs.ts`.
// Tabs live in memory only — a refresh resets to Home (every work object stays
// deep-linkable by URL). The pure functions own all open/close/dedupe logic.
//
// PR3: tabs persist PER BRAND. The provider holds a brandId → WorkspaceTabsState
// map (`MultiBrandTabsState`) and reads the active brand via `useActiveBrand()`.
// Every action is scoped to the active brand's slice via the pure
// `getBrandState`/`withBrandState` helpers, and the context exposes `tabs`/
// `activeId` for the ACTIVE brand only. Switching brands therefore preserves
// each brand's open tabs (and its active tab) for the provider's lifetime.
// Single-brand behavior is identical to before: one slice, same reducers.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useActiveBrand } from "../hooks/useActiveBrand";
import {
  HOME_TAB,
  closeTab as closeTabReducer,
  getBrandState,
  openTab as openTabReducer,
  withBrandState,
  type MultiBrandTabsState,
  type WorkTab,
  type WorkspaceTabsState,
} from "../lib/workspace-tabs";

interface WorkspaceTabsContextValue extends WorkspaceTabsState {
  openTab: (tab: WorkTab) => void;
  closeTab: (id: string) => void;
  /** Activate a tab AND navigate to its route (bar clicks). */
  activateTab: (id: string) => void;
  /**
   * Reconcile the active tab from the current URL WITHOUT navigating. Used by
   * the shell's URL-sync effect so deep-links/route changes mark the right tab.
   */
  syncActiveFromUrl: (id: string) => void;
  /**
   * Register (or re-activate) a tab for the CURRENT url WITHOUT navigating — used
   * by the shell's URL-sync so a deep-link / refresh / external link to an
   * in-shell work object gets a real, closeable tab instead of leaving Home active.
   */
  registerTabFromUrl: (tab: WorkTab) => void;
  /** Change the label of an already-open tab without changing its route or navigation. */
  renameTab: (id: string, label: string) => void;
  /** Convenience: re-activate the pinned Home tab. */
  openHome: () => void;
}

const WorkspaceTabsContext = createContext<WorkspaceTabsContextValue | null>(null);

/** Navigate to a tab's route. Supplied by the shell so the reducer stays pure. */
type NavigateFn = (route: string) => void;

export function WorkspaceTabsProvider({
  children,
  navigate,
}: {
  children: ReactNode;
  /**
   * Optional router navigate. When provided, opening/activating/closing tabs
   * keeps the URL in sync with the active tab (so the destination page renders
   * underneath the persistent tab bar). The reducer itself stays navigation-free.
   */
  navigate?: NavigateFn;
}) {
  // The active brand whose slice the exposed tabs/actions operate on. Tabs are
  // kept per brand so switching brands doesn't reset open work tabs.
  const { activeBrandId } = useActiveBrand();

  const [map, setMap] = useState<MultiBrandTabsState>(() => ({}));

  // Keep the latest navigate + map + active brand in refs so the callbacks stay
  // stable AND so navigation runs OUTSIDE the (pure) state updater — calling
  // navigate in a setState updater double-fires under StrictMode.
  const navigateRef = useRef<NavigateFn | undefined>(navigate);
  navigateRef.current = navigate;
  const mapRef = useRef<MultiBrandTabsState>(map);
  mapRef.current = map;
  const brandIdRef = useRef<string | null | undefined>(activeBrandId);
  brandIdRef.current = activeBrandId;

  // The active brand's slice (lazy Home-only for an unseen brand).
  const state = getBrandState(map, activeBrandId);

  /**
   * Apply a pure transition to the ACTIVE brand's slice, commit it, and (when a
   * navigate is wired) push the resulting active tab's route so the destination
   * page renders under the persistent shell.
   *
   * The URL is the source of truth for which tab is active: the shell's URL-sync
   * effect reconciles `activeId` from `location.pathname`. We still commit the
   * reducer's `activeId` optimistically so the bar feels instant and so the shell
   * works even without a navigate (e.g. isolated unit mounts).
   *
   * Why `apply` reads `mapRef.current` + `setMap(snapshot)` instead of the
   * functional updater form (unlike `applyNoNav` below): it must call `navigate()`
   * AFTER computing the next state but OUTSIDE the setState callback. Navigating
   * inside a setState updater double-fires under React StrictMode (updaters are
   * invoked twice for purity checking), so the navigate has to live out here.
   */
  const apply = useCallback(
    (transition: (prev: WorkspaceTabsState) => WorkspaceTabsState) => {
      const brandId = brandIdRef.current;
      const prev = getBrandState(mapRef.current, brandId);
      const next = transition(prev);
      const nextMap = withBrandState(mapRef.current, brandId, next);
      mapRef.current = nextMap;
      setMap(nextMap);
      const nav = navigateRef.current;
      if (nav) {
        const prevActive = prev.tabs.find((t) => t.id === prev.activeId);
        const nextActive = next.tabs.find((t) => t.id === next.activeId);
        if (nextActive && nextActive.route !== prevActive?.route) {
          nav(nextActive.route);
        }
      }
    },
    [],
  );

  // Commit a pure transition to the active brand's slice WITHOUT navigating.
  // Safe to use the functional updater form here precisely because there is no
  // navigate() — so the StrictMode double-invocation constraint that forces
  // `apply` into the snapshot pattern does not apply (the updater is pure on
  // `prevMap`, so a repeated invocation yields the same `nextMap`).
  const applyNoNav = useCallback(
    (transition: (prev: WorkspaceTabsState) => WorkspaceTabsState) => {
      const brandId = brandIdRef.current;
      setMap((prevMap) => {
        const prev = getBrandState(prevMap, brandId);
        const next = transition(prev);
        if (next === prev) return prevMap;
        const nextMap = withBrandState(prevMap, brandId, next);
        mapRef.current = nextMap;
        return nextMap;
      });
    },
    [],
  );

  const openTab = useCallback(
    (tab: WorkTab) => apply((prev) => openTabReducer(prev, tab)),
    [apply],
  );

  const closeTab = useCallback(
    (id: string) => apply((prev) => closeTabReducer(prev, id)),
    [apply],
  );

  // Bar clicks: activate AND navigate to the tab's route.
  const activateTab = useCallback(
    (id: string) =>
      apply((prev) => (prev.tabs.some((t) => t.id === id) ? { ...prev, activeId: id } : prev)),
    [apply],
  );

  // URL → activeId reconciliation (used by the shell's URL-sync effect). It must
  // NOT navigate — otherwise it would fight the navigation that triggered it. It
  // only moves the active marker to the tab that matches the current route.
  const syncActiveFromUrl = useCallback(
    (id: string) =>
      applyNoNav((prev) =>
        !prev.tabs.some((t) => t.id === id) || prev.activeId === id
          ? prev
          : { ...prev, activeId: id },
      ),
    [applyNoNav],
  );

  // Register a tab for the current URL without navigating (deep-link / refresh).
  // Reuses the open reducer (dedupe-by-route) so it activates an existing tab or
  // appends a new one, but never calls navigate (we are already at that route).
  const registerTabFromUrl = useCallback(
    (tab: WorkTab) => applyNoNav((prev) => openTabReducer(prev, tab)),
    [applyNoNav],
  );

  const renameTab = useCallback(
    (id: string, label: string) =>
      applyNoNav((prev) => {
        const tab = prev.tabs.find((candidate) => candidate.id === id);
        if (!tab || tab.label === label) return prev;
        return { ...prev, tabs: prev.tabs.map((candidate) => candidate.id === id ? { ...candidate, label } : candidate) };
      }),
    [applyNoNav],
  );

  const openHome = useCallback(
    () => apply((prev) => ({ ...prev, activeId: HOME_TAB.id })),
    [apply],
  );

  const value = useMemo<WorkspaceTabsContextValue>(
    () => ({
      tabs: state.tabs,
      activeId: state.activeId,
      openTab,
      closeTab,
      activateTab,
      syncActiveFromUrl,
      registerTabFromUrl,
      renameTab,
      openHome,
    }),
    [state.tabs, state.activeId, openTab, closeTab, activateTab, syncActiveFromUrl, registerTabFromUrl, renameTab, openHome],
  );

  return <WorkspaceTabsContext.Provider value={value}>{children}</WorkspaceTabsContext.Provider>;
}

export function useWorkspaceTabs(): WorkspaceTabsContextValue {
  const ctx = useContext(WorkspaceTabsContext);
  if (!ctx) {
    throw new Error("useWorkspaceTabs must be used within a WorkspaceTabsProvider");
  }
  return ctx;
}

/** Allows detail surfaces to enhance an open shell tab without requiring the shell in isolated renders. */
export function useOptionalWorkspaceTabs(): WorkspaceTabsContextValue | null {
  return useContext(WorkspaceTabsContext);
}
